import 'highcharts/css/highcharts.css';

import { CircularProgress, Typography, useTheme } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { withStyles } from 'tss-react/mui';
import GlobalStyles from '@mui/material/GlobalStyles';

import styles from './highchart.style.js';
const timezone = new Date().getTimezoneOffset();
function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
}
const Chart = ({
  loading,
  classes,
  data,
  dependantFormat,
  toolTipPointFormatter,
  onChartCreated,
  onPointHover,
  onSetExtreme,
  hoveredPoint,
  activeChart,
  maxDomain,
  xMin,
  xMax,
  tickInterval = 6 * 3600 * 1000, // 6 hours
  chartType = 'line',
  disableLegend,
  showNoData = true,
  allowLegendClick = false,
  sharedTooltip = false,
  tickPositions = null,
}) => {
  const theme = useTheme();
  const noData = data.length == 0 || (data[0].data.length === 0 && !loading);
  const [chart, setChart] = React.useState(null);
  React.useEffect(() => {
    if (chart) {
      chart.reflow();
      if (onChartCreated) onChartCreated(chart);
    }
  }, [chart]);

  const handleResize = () => {
    if (chart) {
      chart.reflow();
    }
  };

  const renderTooltip = function(point) {
    return `<span class="highcharts-color-0">●</span> ${
      this.series.name
    }: <strong>${toolTipPointFormatter.call(this)}</strong>`;
  };
  React.useEffect(() => {
    if (chart && chart.xAxis == 'undefined') return;
    if (chart && chart !== activeChart) {
      const ex1 = chart.xAxis[0].getExtremes();
      if (ex1.min != xMin || ex1.max != xMax)
        chart.xAxis[0].setExtremes(xMin, xMax, true, false);
    }
  }, [xMin, xMax]);

  React.useEffect(() => {
    if (chart && hoveredPoint && chart !== activeChart) {
      const allSeries = chart.series;
      allSeries.forEach(series => {
        const reflectingPoint = series.points[hoveredPoint.index];
        if (reflectingPoint) {
          chart.tooltip.refresh(reflectingPoint);
        }
      });
    } else {
      if (chart && !hoveredPoint && chart.tooltip) {
        chart.tooltip.hide();
      }
    }
  }, [hoveredPoint, activeChart]);
  const options = (width, height) => ({
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    chart: {
      // height,
      // width,
      //
      type: chartType,
      height: 300,
      zoomType: 'x',
      styledMode: true,
    },
    legend: {
      enabled: !disableLegend,
    },
    series: data,
    xAxis: {
      type: 'datetime',
      gridLineWidth: 1,
      tickInterval: tickInterval, // one week
      tickWidth: 0,
      minorTickInterval: 'auto',
      events: {
        afterSetExtremes: function(event) {
          var xMin = event.min;
          var xMax = event.max;
          if (onSetExtreme) onSetExtreme(xMin, xMax, chart);
        },
      },
    },
    time: {
      timezoneOffset: timezone,
    },
    yAxis: [
      {
        labels: {
          formatter: dependantFormat,
          enabled: showNoData ? true : !noData,
        },
        max: maxDomain || 5,
        tickPositions: tickPositions,
        min: 0,
        title: null,
        minorTickInterval: 'auto',
      },

      {
        /*         labels: {
          enabled: false,
        },
        max: maxDomain || 5,
        min: 0,

        tickPositions: tickPositions,
        minorTickInterval: 'auto', */
        title: null,
        opposite: true,
      },
    ],
    tooltip: {
      pointFormatter: toolTipPointFormatter && renderTooltip,
      borderColor: theme.palette.divider,
      crosshairs: true,
      shared: sharedTooltip,
    },
    plotOptions: {
      column: {
        stacking: 'normal',
      },
      series: {
        events: {
          legendItemClick:
            !allowLegendClick &&
            function() {
              return false;
            },
        },
        point: {
          events: {
            mouseOver: function(event) {
              if (onPointHover) onPointHover(this, chart);
            },

            mouseOut: function(event) {
              if (onPointHover) onPointHover(null, chart);
            },
          },
        },
      },
    },
  });
  return (
    <div className={classes.graphContainer}>
      <GlobalStyles
        styles={{
          '.highcharts-container.highcharts-container': {
            '.highcharts-root': {
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            },
            '.highcharts-no-grid  .highcharts-grid-line, .highcharts-no-grid .highcharts-axis-line, .highcharts-no-grid .highcharts-tick': {
              strokeWidth: 0,
            },
            '.highcharts-background': {
              fill: theme.palette.background.paper,
            },

            '.highcharts-container text': {
              fill: theme.palette.text.secondary,
            },

            '.highcharts-subtitle, .highcharts-credits, .highcharts-axis-title': {
              fillOpacity: '0.7',
            },

            '.highcharts-axis-line': {
              stroke: theme.palette.text.secondary,
              strokeOpacity: '0.2',
              strokeWidth: 1,
            },
            '.highcharts-grid-line': {
              stroke: theme.palette.text.secondary,
              strokeOpacity: '0.2',
              strokeWidth: 1,
            },
            '.highcharts-minor-grid-line': {
              stroke: theme.palette.text.secondary,
              strokeOpacity: '0.06',
              strokeWidth: 1,
            },
            '.highcharts-tooltip-box': {
              fill: theme.palette.background.paper,
            },
            '.highcharts-tooltip': {
              // stroke: [fade(theme.palette.divider, 0.3), '!important'],
            },
            '.highcharts-tooltip text': {
              fill: theme.palette.contrast.main,
            },

            // '.highcharts-color-0': {
            //   fill: ['limegreen', '!important'],
            //   strokeWidth: [1, '!important'],
            //   stroke: ['limegreen', '!important'],
            // },
            '.highcharts-graph': {
              strokeWidth: 1.5,
            },
            '.highcharts-points-hover': {
              stroke: 'red',
            },

            '.highcharts-button': {
              fill: theme.palette.background.paper,
              stroke: theme.palette.text.secondary,
            },

            '.highcharts-legend-item > text': {
              fill: theme.palette.text.secondary,
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif; !important',
              fontWeight: 'normal',
            },

            '.highcharts-column-series > .highcharts-point': {
              strokeWidth: 0,
            },
            '.highcharts-axis-labels': {
              fill: theme.palette.text.secondary,
            },
          },
        }}
      />
      <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => {}}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options()}
          callback={setChart}
        />
      </ErrorBoundary>

      {showNoData && noData && (
        <div className={classes.noData}>
          <Typography
            className={classes.noDataText}
            variant="caption"
            color="textSecondary"
          >
            NO DATA AVAILABLE
          </Typography>
        </div>
      )}
    </div>
  );
};

export default withStyles(Chart, styles, { withTheme: true });
